import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useConsent } from "../../contexts/ConsentContext";
import { X } from "lucide-react";
import { Switch } from "antd";
import { useLanguage } from "../../contexts/LanguageContext";

const CookieSettingsModal = ({ isOpen, onClose }) => {
  const { consent, saveConsent } = useConsent();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const { translations } = useLanguage();

  useEffect(() => {
    console.log("Modal opened. Current consent in context:", consent);
    if (consent) {
      setAnalytics(consent.categories.analytics);
      setMarketing(consent.categories.marketing);
    }
  }, [consent]);

  if (!isOpen) return null;

  const handleSavePreferences = () => {
    console.log("--- handleSavePreferences START ---");
    console.log("Current state - analytics:", analytics, "marketing:", marketing);
    console.log("saveConsent function exists:", typeof saveConsent === "function");

    try {
      const consentData = {
        categories: {
          necessary: true,
          analytics,
          marketing,
        },
        allAccepted: analytics && marketing,
        createdAt: new Date().toISOString(),
      };
      
      console.log("Calling saveConsent with:", consentData);
      saveConsent(consentData);
      console.log("saveConsent call completed (it is async, but triggered)");
      onClose?.();
    } catch (err) {
      console.error("CRITICAL ERROR in handleSavePreferences:", err);
    }
  };

  const handleRejectAll = () => {
    const consentData = {
      categories: {
        necessary: true,
        analytics: false,
        marketing: false,
      },
      allAccepted: false,
      createdAt: new Date().toISOString(),
    };
    saveConsent(consentData);
    onClose?.();
  };

  const title = translations.cookieSettingsModal?.title || "Manage Cookies";
  const subtitle = translations.cookieSettingsModal?.subtitle || "About Your Privacy";
  const paragraph1 = translations.cookieSettingsModal?.paragraph1 || "We use cookies and similar technologies to operate, secure, and improve the Supreme Edits platform. Some cookies are necessary for the platform to function properly, while others help us understand how the platform is used so we can continuously improve features, content, and the overall experience for DJs.";
  const paragraph2 = translations.cookieSettingsModal?.paragraph2 || "You can choose which categories of cookies you allow below. You can update your preferences at any time via the 'Manage Cookie Consent' option available in your dashboard."
  const paragraph3 =
    translations.cookieSettingsModal?.paragraph3 ||
    "For more information, please review our ";
  const type1 =
    translations.cookieSettingsModal?.type1 || "Necessary Cookies";
  const type2 =
    translations.cookieSettingsModal?.type2 || "Analytics Cookies";
  const type3 = translations.cookieSettingsModal?.type3 || "Marketing Cookies";
  const description1 = translations.cookieSettingsModal?.description1 || "Necessary cookies help make a website usable by enabing basic functions like page navigation and access to secure of the website. The website cannot function properly without these cookies.";
  const description2 = translations.cookieSettingsModal?.description2 || "Statistic cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously.";
  const description3 = translations.cookieSettingsModal?.description3 || "Marketing cookies are used to track visitors across webstites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.";
  const btn1 = translations.cookieSettingsModal?.btn1 || "Back";
  const btn2 = translations.cookieSettingsModal?.btn2 || "Reject All";
  const btn3 = translations.cookieSettingsModal?.btn3 || "Save Preferences";

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/70"
        onClick={() => onClose?.()}
        aria-label="Close cookie settings"
      />
      <div
        className={`relative flex flex-col w-full max-w-[640px] h-[calc(100vh-2rem)] max-h-[757px] bg-[#010204] text-white shadow-[5px_15px_20px_rgba(0,0,0,0.25)] overflow-hidden p-5 sm:p-[30px]`}
      >
        <button
          type="button"
          className="absolute right-6 top-2 text-[#8D8D8D] rounded-full border border-[#8D8D8D] hover:text-white hover:border-white transition-colors"
          onClick={() => onClose?.()}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <h1 className="font-bold text-xl text-transparent bg-[linear-gradient(180deg,#52FAF0_31%,#197AD0_91.1%)] bg-clip-text mb-4">
          {title}
        </h1>

        <div className="flex-1 overflow-y-auto pr-1">
          <h3 className="font-bold text-lg text-[#A5A5A5] mb-2">{subtitle}</h3>
          <p className="text-[13px] sm:text-sm text-[#A5A5A5] mb-4">
            {paragraph1}
          </p>
          <p className="text-[13px] sm:text-sm text-[#A5A5A5] mb-4">
            {paragraph2}
          </p>
          <p className="text-[13px] sm:text-sm text-[#A5A5A5] mb-4">
            {paragraph3}{" "}
            <a href="/privacy-policy" className="underline">
              {" "}
              Privacy Policy.{" "}
            </a>
          </p>

          <div className="border-2 border-[#6E7191] rounded-[10px] mb-8">
            <div className="flex items-center justify-between border-b-2 border-[#6E7191] p-2 sm:py-4 sm:px-5">
              <div className="">
                <div className="flex justify-between">
                  <h3 className="font-bold text-sm bg-gradient-to-b from-[#52FAF0] to-[#1593D6] text-transparent bg-clip-text">
                    {type1}
                  </h3>
                  <span className="font-bold text-[9px] bg-gradient-to-b from-[#52FAF0] to-[#1593D6] text-transparent bg-clip-text px-2 py-1 rounded-full border border-[#52FAF0]">
                    Always Active
                  </span>
                </div>

                <p className="font-medium text-[13px] sm:text-sm text-[#A5A5A5] tracking-tighter sm:tracking-normal mt-1 xs:pr-12">
                  {description1}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between border-b-2 border-[#6E7191] p-2 sm:py-4 sm:px-5">
              <div>
                <div className="flex justify-between">
                  <h3 className="font-bold text-sm bg-gradient-to-b from-[#52FAF0] to-[#1593D6] text-transparent bg-clip-text">
                    {type2}
                  </h3>
                  <Switch
                    checked={analytics}
                    onChange={(val) => {
                      console.log("TOGGLE (Mobile): Analytics =", val);
                      setAnalytics(val);
                    }}
                    className="block xs:hidden"
                  />
                </div>
                <p className="font-medium text-[13px] sm:text-sm text-[#A5A5A5] tracking-tighter sm:tracking-normal mt-1 xs:pr-2">
                  {description2}
                </p>
              </div>
                <Switch
                  checked={analytics}
                  onChange={(val) => {
                    console.log("TOGGLE: Analytics =", val);
                    setAnalytics(val);
                  }}
                  className="hidden xs:block"
                />
            </div>

            <div className="flex items-center justify-between p-2 sm:py-4 sm:px-5">
              <div>
                <div className="flex justify-between">
                  <h3 className="font-bold text-sm bg-gradient-to-b from-[#52FAF0] to-[#1593D6] text-transparent bg-clip-text">
                    {type3}
                  </h3>
                  <Switch
                    checked={marketing}
                    onChange={(val) => {
                      console.log("TOGGLE (Mobile): Marketing =", val);
                      setMarketing(val);
                    }}
                    className="block xs:hidden"
                  />
                </div>
                <p className="font-medium text-[13px] sm:text-sm text-[#A5A5A5] tracking-tighter sm:tracking-normal mt-1 xs:pr-2">
                  {description3}
                </p>
              </div>
                <Switch
                  checked={marketing}
                  onChange={(val) => {
                    console.log("TOGGLE: Marketing =", val);
                    setMarketing(val);
                  }}
                  className="hidden xs:block"
                />
            </div>
          </div>
        </div>

        <div className="flex gap-3 sm:gap-7 justify-end pt-4 pr-5">
          <button
            type="button"
            onClick={() => onClose?.()}
            className="font-bold text-xs sm:text-sm text-white underline hidden xs:block"
          >
            {btn1}
          </button>
          <button
            type="button"
            onClick={handleRejectAll}
            className="flex items-center justify-center bg-[#000101] 
              font-poppins text-xs sm:text-sm text-white font-semibold rounded-[2px] relative z-10 transition-all duration-300 
              w-[170px] h-[34px]
              border-2 border-transparent bg-gradient-to-b from-[#52FAF0] via-[#36BAE0] to-[#197AD0] bg-origin-border"
            style={{
              backgroundImage:
                "linear-gradient(#000101, #000101), linear-gradient(180deg, #52FAF0 1.84%, #36BAE0 21.65%, #197AD0 106.4%)",
              backgroundClip: "padding-box, border-box",
              backgroundOrigin: "border-box",
            }}
          >
            {btn2}
          </button>
          <button
            type="button"
            onClick={handleSavePreferences}
            className="w-[170px] h-[34px] font-bold text-xs sm:text-sm rounded-[2px] bg-gradient-to-r from-cyan-500 to-blue-500 text-black hover:opacity-90"
          >
            {btn3}
          </button>
        </div>
        <div
          className="
            absolute top-[90%] left-[5%]
            w-[85%] h-[180px]
            opacity-60
            rounded-[50%]
            blur-[120px]
            pointer-events-none
            bg-[linear-gradient(180deg,#4EEEE4_0%,#1874C0_34.14%)]
            z-10
          "
        />
      </div>
    </div>,
    document.body,
  );
};

export default CookieSettingsModal;
